import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { 
  ArrowLeft, 
  ArrowRight, 
  Check, 
  Trophy, 
  Users, 
  Calendar, 
  MapPin, 
  DollarSign,
  Award,
  Zap,
  Image,
  Loader2,
  Search,
  UsersRound,
  X,
  Upload,
  ClipboardList,
  CreditCard,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Sport formats data (without Football and Carrom, added Crazy Hide & Seek)
const SPORTS = [
  { id: 'table_tennis', name: 'Table Tennis', icon: '🏓', formats: ['singles', 'doubles'] },
  { id: 'badminton', name: 'Badminton', icon: '🏸', formats: ['singles', 'doubles', 'mixed_doubles'] },
  { id: 'tennis', name: 'Tennis', icon: '🎾', formats: ['singles', 'doubles', 'mixed_doubles'] },
  { id: 'cricket', name: 'Cricket', icon: '🏏', formats: ['double_wicket', '5_side', '11_side'] },
  { id: 'volleyball', name: 'Volleyball', icon: '🏐', formats: ['6_side', '4_side'] },
  { id: 'basketball', name: 'Basketball', icon: '🏀', formats: ['5v5', '3v3'] },
  { id: 'crazy_hide_seek', name: 'Crazy Hide & Seek', icon: '🙈', formats: ['team', 'individual'] },
];

// Materials checklist by sport
const MATERIALS_CHECKLIST = {
  table_tennis: {
    essential: [
      { id: 'tt_table', name: 'Table Tennis Table', icon: '🏓' },
      { id: 'tt_net', name: 'Table Tennis Net', icon: '🥅' },
      { id: 'tt_paddles', name: 'Paddles (2-4)', icon: '🏓' },
      { id: 'tt_balls', name: 'Table Tennis Balls (6-12)', icon: '⚪' },
    ],
    optional: [
      { id: 'first_aid', name: 'First Aid Kit', icon: '🩹' },
      { id: 'towels', name: 'Towels', icon: '🧻' },
      { id: 'scoreboard', name: 'Scoreboard', icon: '📊' },
    ],
  },
  badminton: {
    essential: [
      { id: 'bd_net', name: 'Badminton Net', icon: '🥅' },
      { id: 'bd_rackets', name: 'Rackets (2-4)', icon: '🏸' },
      { id: 'bd_shuttles', name: 'Shuttlecocks (12-24)', icon: '🏸' },
      { id: 'bd_posts', name: 'Net Posts', icon: '🔩' },
    ],
    optional: [
      { id: 'first_aid', name: 'First Aid Kit', icon: '🩹' },
      { id: 'towels', name: 'Towels', icon: '🧻' },
      { id: 'court_markers', name: 'Court Line Markers', icon: '📏' },
    ],
  },
  tennis: {
    essential: [
      { id: 'tn_net', name: 'Tennis Net', icon: '🥅' },
      { id: 'tn_rackets', name: 'Tennis Rackets (2-4)', icon: '🎾' },
      { id: 'tn_balls', name: 'Tennis Balls (6-12)', icon: '🎾' },
    ],
    optional: [
      { id: 'first_aid', name: 'First Aid Kit', icon: '🩹' },
      { id: 'ball_hopper', name: 'Ball Hopper', icon: '🧺' },
      { id: 'scoreboard', name: 'Scoreboard', icon: '📊' },
    ],
  },
  cricket: {
    essential: [
      { id: 'cr_bat', name: 'Cricket Bat', icon: '🏏' },
      { id: 'cr_ball', name: 'Cricket Ball', icon: '🔴' },
      { id: 'cr_stumps', name: 'Stumps & Bails', icon: '🎯' },
      { id: 'cr_pads', name: 'Batting Pads', icon: '🦵' },
      { id: 'cr_gloves', name: 'Batting Gloves', icon: '🧤' },
    ],
    optional: [
      { id: 'first_aid', name: 'First Aid Kit', icon: '🩹' },
      { id: 'helmet', name: 'Helmet', icon: '⛑️' },
      { id: 'boundary_flags', name: 'Boundary Flags', icon: '🚩' },
    ],
  },
  volleyball: {
    essential: [
      { id: 'vb_net', name: 'Volleyball Net', icon: '🥅' },
      { id: 'vb_ball', name: 'Volleyball', icon: '🏐' },
      { id: 'vb_posts', name: 'Net Posts', icon: '🔩' },
    ],
    optional: [
      { id: 'first_aid', name: 'First Aid Kit', icon: '🩹' },
      { id: 'scoreboard', name: 'Scoreboard', icon: '📊' },
      { id: 'knee_pads', name: 'Knee Pads', icon: '🦵' },
    ],
  },
  basketball: {
    essential: [
      { id: 'bb_ball', name: 'Basketball', icon: '🏀' },
      { id: 'bb_hoop', name: 'Basketball Hoop', icon: '🏀' },
    ],
    optional: [
      { id: 'first_aid', name: 'First Aid Kit', icon: '🩹' },
      { id: 'scoreboard', name: 'Scoreboard', icon: '📊' },
      { id: 'whistle', name: 'Referee Whistle', icon: '📣' },
    ],
  },
  crazy_hide_seek: {
    essential: [
      { id: 'hs_blindfolds', name: 'Blindfolds', icon: '👀' },
      { id: 'hs_timer', name: 'Timer/Stopwatch', icon: '⏱️' },
      { id: 'hs_boundary', name: 'Boundary Markers', icon: '🚧' },
    ],
    optional: [
      { id: 'first_aid', name: 'First Aid Kit', icon: '🩹' },
      { id: 'walkie_talkie', name: 'Walkie Talkies', icon: '📻' },
      { id: 'flashlights', name: 'Flashlights', icon: '🔦' },
    ],
  },
};

const SCOPES = [
  { id: 'group', name: 'Group', description: 'Only group members can participate' },
  { id: 'local', name: 'Local', description: 'Open to local participants' },
  { id: 'state', name: 'State', description: 'State-level tournament' },
  { id: 'national', name: 'National', description: 'National-level tournament' },
  { id: 'private', name: 'Private', description: 'Invite-only tournament' },
];

const TOURNAMENT_TYPES = [
  { id: 'knockout', name: 'Knockout', description: 'Single elimination' },
  { id: 'round_robin', name: 'Round Robin', description: 'Everyone plays everyone' },
  { id: 'league', name: 'League', description: 'Points-based league' },
  { id: 'swiss', name: 'Swiss', description: 'Swiss system pairing' },
];

const PAYMENT_TYPES = [
  { id: 'cash', name: 'Cash' },
  { id: 'upi', name: 'UPI' },
  { id: 'bank_transfer', name: 'Bank Transfer' },
  { id: 'cheque', name: 'Cheque' },
  { id: 'neft', name: 'NEFT' },
  { id: 'rtgs', name: 'RTGS' },
];

const STEPS = [
  { id: 1, title: 'Sport Format', icon: Trophy },
  { id: 2, title: 'Challenge Details', icon: Users },
  { id: 3, title: 'Event Dates', icon: Calendar },
  { id: 4, title: 'Tournament Type', icon: Award },
  { id: 5, title: 'Scoring Rules', icon: Zap },
  { id: 6, title: 'Venue', icon: MapPin },
  { id: 7, title: 'Entry Fee', icon: DollarSign },
  { id: 8, title: 'Prizes', icon: Award },
  { id: 9, title: 'Banner', icon: Image },
  { id: 10, title: 'Checklist', icon: ClipboardList },
  { id: 11, title: 'Payment Details', icon: CreditCard },
  { id: 12, title: 'Review', icon: Check },
];

export default function CreateChallengePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [groupSearchQuery, setGroupSearchQuery] = useState('');
  const [groupSearchResults, setGroupSearchResults] = useState([]);
  const [searchingGroups, setSearchingGroups] = useState(false);
  const [showGroupDropdown, setShowGroupDropdown] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const groupSearchRef = useRef(null);
  const bannerInputRef = useRef(null);
  
  // Form data
  const [formData, setFormData] = useState({
    // Step 1: Sport
    sport: '',
    sport_format: '',
    format_type: 'singles',
    
    // Step 2: Details
    name: '',
    description: '',
    scope: 'group',
    group_id: '',
    group_name: '',
    min_teams: 2,
    max_teams: 32,
    
    // Step 3: Dates
    event_start: '',
    event_end: '',
    registration_start: '',
    registration_end: '',
    
    // Step 4: Tournament Type
    tournament_type: 'knockout',
    
    // Step 5: Scoring
    sets_to_win: 1,
    points_per_set: 21,
    deuce_enabled: true,
    max_points_per_set: 30,
    
    // Step 6: Venue
    venue_name: '',
    venue_address: '',
    city: '',
    state: '',
    country: 'India',
    
    // Step 7: Entry Fee
    is_free: true,
    entry_fee: 0,
    entry_fee_currency: 'INR',
    registration_processing_fee: 0,
    final_fees_total: 0,
    rewards_description: '',
    
    // Step 8: Prizes
    prizes: [
      { position: 1, title: '1st Place', prize: '' },
      { position: 2, title: '2nd Place', prize: '' },
      { position: 3, title: '3rd Place', prize: '' },
    ],
    
    // Step 9: Banner
    banner_image: '',
    banner_file: null,
    logo_url: '',
    
    // Fee Calculation (calculated from entry_fee)
    base_entry_fee: 0, // Back-calculated base fee (before processing fee)
    
    // Step 10: Materials Checklist
    materials_checklist: [],
    
    // Step 11: Payment Details
    customer_name: '',
    customer_address: '',
    customer_vendor_code: '',
    customer_gst_no: '',
    customer_po_no: '',
    po_date: '',
    po_amount: 0,
    advance_payment_amount: 0,
    advance_payment_type: '',
    final_payment_amount: 0,
    final_payment_type: '',
    invoice_no: '',
    invoice_date: '',
    other_details: '',
    
    // Crazy Rules
    crazy_rules_enabled: false,
    crazy_rules: [],
  });

  // Calculate base fee and processing fee from the TOTAL amount entered
  // User enters the FINAL amount they want to charge (what users see in mobile app)
  // We back-calculate the base fee by removing the 20% + 18% GST (i.e., divide by 1.236)
  useEffect(() => {
    if (!formData.is_free && formData.entry_fee > 0) {
      // The entry_fee is the TOTAL amount (what mobile app users pay)
      const totalAmount = formData.entry_fee;
      
      // Back-calculate: Processing fee = 20% of base + 18% GST on that = base * 0.20 * 1.18 = base * 0.236
      // So: total = base + base * 0.236 = base * 1.236
      // Therefore: base = total / 1.236
      const baseFee = totalAmount / 1.236;
      const processingFee = totalAmount - baseFee;
      
      setFormData(prev => ({
        ...prev,
        base_entry_fee: Math.round(baseFee * 100) / 100,
        registration_processing_fee: Math.round(processingFee * 100) / 100,
        final_fees_total: Math.round(totalAmount * 100) / 100,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        base_entry_fee: 0,
        registration_processing_fee: 0,
        final_fees_total: 0,
      }));
    }
  }, [formData.entry_fee, formData.is_free]);

  // Debounced group search
  useEffect(() => {
    const searchGroups = async () => {
      if (groupSearchQuery.length < 1) {
        setGroupSearchResults([]);
        return;
      }
      
      setSearchingGroups(true);
      try {
        const response = await axios.get(`${API_URL}/api/groups/search/autocomplete?q=${encodeURIComponent(groupSearchQuery)}&limit=10`);
        setGroupSearchResults(response.data.groups || []);
      } catch (error) {
        console.error('Error searching groups:', error);
        setGroupSearchResults([]);
      } finally {
        setSearchingGroups(false);
      }
    };

    const debounceTimer = setTimeout(searchGroups, 300);
    return () => clearTimeout(debounceTimer);
  }, [groupSearchQuery]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (groupSearchRef.current && !groupSearchRef.current.contains(event.target)) {
        setShowGroupDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSportSelect = (sportId) => {
    const sport = SPORTS.find(s => s.id === sportId);
    setFormData(prev => ({
      ...prev,
      sport: sportId,
      sport_format: sport?.name || '',
      format_type: sport?.formats[0] || 'singles',
      materials_checklist: [], // Reset checklist when sport changes
    }));
  };

  const handleGroupSelect = (group) => {
    setFormData(prev => ({
      ...prev,
      group_id: group.id,
      group_name: group.name,
    }));
    setGroupSearchQuery(group.name);
    setShowGroupDropdown(false);
  };

  const clearGroupSelection = () => {
    setFormData(prev => ({
      ...prev,
      group_id: '',
      group_name: '',
    }));
    setGroupSearchQuery('');
  };

  const handlePrizeChange = (index, value) => {
    const newPrizes = [...formData.prizes];
    newPrizes[index].prize = value;
    setFormData(prev => ({ ...prev, prizes: newPrizes }));
  };

  const handleBannerUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }
    
    setUploadingBanner(true);
    
    try {
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      
      // For now, store the file and preview locally
      // In production, you'd upload to cloud storage
      setFormData(prev => ({
        ...prev,
        banner_image: previewUrl,
        banner_file: file,
      }));
      
      toast.success('Banner image selected');
    } catch (error) {
      toast.error('Failed to process image');
    } finally {
      setUploadingBanner(false);
    }
  };

  const handleChecklistToggle = (itemId) => {
    setFormData(prev => {
      const currentChecklist = prev.materials_checklist || [];
      if (currentChecklist.includes(itemId)) {
        return { ...prev, materials_checklist: currentChecklist.filter(id => id !== itemId) };
      } else {
        return { ...prev, materials_checklist: [...currentChecklist, itemId] };
      }
    });
  };

  const validateStep = () => {
    switch (currentStep) {
      case 1:
        if (!formData.sport) {
          toast.error('Please select a sport');
          return false;
        }
        return true;
      case 2:
        if (!formData.name.trim()) {
          toast.error('Challenge name is required');
          return false;
        }
        if (formData.scope === 'group' && !formData.group_id) {
          toast.error('Please select a group');
          return false;
        }
        return true;
      case 3:
        if (!formData.event_start || !formData.event_end) {
          toast.error('Event dates are required');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep()) {
      setCurrentStep(prev => Math.min(prev + 1, STEPS.length));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      let bannerUrl = formData.banner_image;
      
      // If we have a banner file (local blob), upload it first
      if (formData.banner_file) {
        const uploadFormData = new FormData();
        uploadFormData.append('file', formData.banner_file);
        
        const uploadResponse = await axios.post(`${API_URL}/api/upload/banner`, uploadFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        // Use the returned URL
        bannerUrl = `${API_URL}${uploadResponse.data.url}`;
      }
      
      const challengeData = {
        ...formData,
        banner_image: bannerUrl,
        admin_user_id: user?.id,
        admin_user_name: user?.name,
        // entry_fee is the TOTAL amount (what user pays in Lunipin mobile app)
        // formData.entry_fee is already the total amount entered by admin
        entry_fee: formData.entry_fee,
        // base_entry_fee is the back-calculated amount (for accounting)
        base_entry_fee: formData.base_entry_fee || formData.entry_fee,
        // final_fees_total equals entry_fee (they are the same now)
        final_fees_total: formData.entry_fee,
      };
      
      // Remove the file object before sending
      delete challengeData.banner_file;
      
      await axios.post(`${API_URL}/api/admin/challenges`, challengeData);
      toast.success('Challenge created successfully!');
      navigate('/events');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create challenge');
    } finally {
      setLoading(false);
    }
  };

  const selectedSport = SPORTS.find(s => s.id === formData.sport);
  const sportChecklist = MATERIALS_CHECKLIST[formData.sport] || { essential: [], optional: [] };
  const totalChecklistItems = sportChecklist.essential.length + sportChecklist.optional.length;
  const checkedItems = formData.materials_checklist?.length || 0;

  return (
    <div className="max-w-4xl mx-auto" data-testid="create-challenge-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/events')}
          className="text-slate-400"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-white">Create Challenge</h1>
          <p className="text-sm text-slate-500">Step {currentStep} of {STEPS.length}</p>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="mb-8 overflow-x-auto">
        <div className="flex items-center gap-1 min-w-max pb-2">
          {STEPS.map((step, index) => {
            const Icon = step.icon;
            const isActive = currentStep === step.id;
            const isCompleted = currentStep > step.id;
            
            return (
              <div key={step.id} className="flex items-center">
                <button
                  onClick={() => setCurrentStep(step.id)}
                  className={cn(
                    "flex items-center gap-1 px-2 py-1.5 rounded-lg transition-all text-xs",
                    isActive && "bg-green-500/20 text-green-400 border border-green-500/50",
                    isCompleted && "bg-slate-800 text-green-400",
                    !isActive && !isCompleted && "bg-slate-900 text-slate-500"
                  )}
                >
                  <Icon className="w-3 h-3" />
                  <span className="hidden lg:inline">{step.title}</span>
                </button>
                {index < STEPS.length - 1 && (
                  <div className={cn(
                    "w-2 h-0.5 mx-0.5",
                    isCompleted ? "bg-green-500" : "bg-slate-700"
                  )} />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Step Content */}
      <Card className="bg-slate-900/50 border-slate-800 mb-6">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            {(() => { const Icon = STEPS[currentStep - 1].icon; return <Icon className="w-5 h-5 text-green-400" />; })()}
            {STEPS[currentStep - 1].title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Step 1: Sport Format */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div>
                <Label className="text-slate-300 mb-3 block">Select Sport</Label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {SPORTS.map(sport => (
                    <button
                      key={sport.id}
                      type="button"
                      onClick={() => handleSportSelect(sport.id)}
                      className={cn(
                        "p-4 rounded-lg border text-center transition-all",
                        formData.sport === sport.id
                          ? "border-green-500 bg-green-500/20 text-white"
                          : "border-slate-700 bg-slate-800 text-slate-300 hover:border-slate-600"
                      )}
                    >
                      <span className="text-3xl block mb-2">{sport.icon}</span>
                      <span className="text-sm font-medium">{sport.name}</span>
                    </button>
                  ))}
                </div>
              </div>
              
              {selectedSport && (
                <div>
                  <Label className="text-slate-300 mb-3 block">Format Type</Label>
                  <div className="flex flex-wrap gap-3">
                    {selectedSport.formats.map(format => (
                      <button
                        key={format}
                        type="button"
                        onClick={() => handleChange('format_type', format)}
                        className={cn(
                          "px-4 py-2 rounded-lg border transition-all capitalize",
                          formData.format_type === format
                            ? "border-green-500 bg-green-500/20 text-white"
                            : "border-slate-700 bg-slate-800 text-slate-300 hover:border-slate-600"
                        )}
                      >
                        {format.replace(/_/g, ' ')}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Challenge Details */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div>
                <Label className="text-slate-300">Challenge Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="Enter challenge name"
                  className="bg-slate-800 border-slate-700 mt-2"
                />
              </div>
              
              <div>
                <Label className="text-slate-300">Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="Describe your challenge..."
                  className="bg-slate-800 border-slate-700 mt-2"
                  rows={3}
                />
              </div>
              
              <div>
                <Label className="text-slate-300 mb-3 block">Scope</Label>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                  {SCOPES.map(scope => (
                    <button
                      key={scope.id}
                      type="button"
                      onClick={() => handleChange('scope', scope.id)}
                      className={cn(
                        "p-3 rounded-lg border text-center transition-all",
                        formData.scope === scope.id
                          ? "border-green-500 bg-green-500/20 text-white"
                          : "border-slate-700 bg-slate-800 text-slate-300 hover:border-slate-600"
                      )}
                    >
                      <span className="text-sm font-medium">{scope.name}</span>
                    </button>
                  ))}
                </div>
              </div>
              
              {formData.scope === 'group' && (
                <div ref={groupSearchRef} className="relative">
                  <Label className="text-slate-300">Select Group *</Label>
                  <div className="relative mt-2">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      value={groupSearchQuery}
                      onChange={(e) => {
                        setGroupSearchQuery(e.target.value);
                        setShowGroupDropdown(true);
                      }}
                      onFocus={() => setShowGroupDropdown(true)}
                      placeholder="Type to search groups..."
                      className="bg-slate-800 border-slate-700 pl-10 pr-10"
                    />
                    {formData.group_id && (
                      <button
                        onClick={clearGroupSelection}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  
                  {showGroupDropdown && (groupSearchResults.length > 0 || searchingGroups) && (
                    <div className="absolute z-50 w-full mt-1 bg-slate-800 border border-slate-700 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                      {searchingGroups ? (
                        <div className="p-4 text-center text-slate-400">
                          <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                        </div>
                      ) : (
                        groupSearchResults.map(group => (
                          <button
                            key={group.id}
                            type="button"
                            onClick={() => handleGroupSelect(group)}
                            className={cn(
                              "w-full flex items-center gap-3 p-3 hover:bg-slate-700 transition-colors text-left",
                              formData.group_id === group.id && "bg-green-500/20"
                            )}
                          >
                            <div className="w-10 h-10 rounded-full bg-indigo-500/20 flex items-center justify-center">
                              {group.logo_url ? (
                                <img src={group.logo_url} alt="" className="w-10 h-10 rounded-full object-cover" />
                              ) : (
                                <UsersRound className="w-5 h-5 text-indigo-400" />
                              )}
                            </div>
                            <div className="flex-1">
                              <p className="text-white font-medium">{group.name}</p>
                              <p className="text-xs text-slate-400">{group.member_count || 0} members</p>
                            </div>
                            {formData.group_id === group.id && (
                              <Check className="w-5 h-5 text-green-400" />
                            )}
                          </button>
                        ))
                      )}
                      {!searchingGroups && groupSearchResults.length === 0 && groupSearchQuery && (
                        <div className="p-4 text-center text-slate-400">
                          No groups found
                        </div>
                      )}
                    </div>
                  )}
                  
                  {formData.group_name && (
                    <p className="mt-2 text-sm text-green-400">
                      Selected: {formData.group_name}
                    </p>
                  )}
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Min Teams Needed</Label>
                  <Input
                    type="number"
                    value={formData.min_teams}
                    onChange={(e) => handleChange('min_teams', parseInt(e.target.value) || 2)}
                    min={2}
                    className="bg-slate-800 border-slate-700 mt-2"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Max Teams</Label>
                  <Input
                    type="number"
                    value={formData.max_teams}
                    onChange={(e) => handleChange('max_teams', parseInt(e.target.value) || 32)}
                    min={2}
                    className="bg-slate-800 border-slate-700 mt-2"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Event Dates */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Event Start *</Label>
                  <Input
                    type="datetime-local"
                    value={formData.event_start}
                    onChange={(e) => handleChange('event_start', e.target.value)}
                    className="bg-slate-800 border-slate-700 mt-2"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Event End *</Label>
                  <Input
                    type="datetime-local"
                    value={formData.event_end}
                    onChange={(e) => handleChange('event_end', e.target.value)}
                    className="bg-slate-800 border-slate-700 mt-2"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Registration Start</Label>
                  <Input
                    type="datetime-local"
                    value={formData.registration_start}
                    onChange={(e) => handleChange('registration_start', e.target.value)}
                    className="bg-slate-800 border-slate-700 mt-2"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Registration End</Label>
                  <Input
                    type="datetime-local"
                    value={formData.registration_end}
                    onChange={(e) => handleChange('registration_end', e.target.value)}
                    className="bg-slate-800 border-slate-700 mt-2"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Tournament Type */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {TOURNAMENT_TYPES.map(type => (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => handleChange('tournament_type', type.id)}
                    className={cn(
                      "p-4 rounded-lg border text-left transition-all",
                      formData.tournament_type === type.id
                        ? "border-green-500 bg-green-500/20"
                        : "border-slate-700 bg-slate-800 hover:border-slate-600"
                    )}
                  >
                    <p className="text-white font-medium">{type.name}</p>
                    <p className="text-sm text-slate-400">{type.description}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 5: Scoring Rules */}
          {currentStep === 5 && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Sets to Win</Label>
                  <Select value={formData.sets_to_win.toString()} onValueChange={(v) => handleChange('sets_to_win', parseInt(v))}>
                    <SelectTrigger className="bg-slate-800 border-slate-700 mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="1">Best of 1</SelectItem>
                      <SelectItem value="2">Best of 3</SelectItem>
                      <SelectItem value="3">Best of 5</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-300">Points Per Set</Label>
                  <Input
                    type="number"
                    value={formData.points_per_set}
                    onChange={(e) => handleChange('points_per_set', parseInt(e.target.value) || 21)}
                    className="bg-slate-800 border-slate-700 mt-2"
                  />
                </div>
              </div>
              
              <div className="flex items-center justify-between p-4 bg-slate-800 rounded-lg">
                <div>
                  <p className="text-white font-medium">Enable Deuce</p>
                  <p className="text-sm text-slate-400">Require 2-point lead to win</p>
                </div>
                <Switch
                  checked={formData.deuce_enabled}
                  onCheckedChange={(v) => handleChange('deuce_enabled', v)}
                />
              </div>
              
              {formData.deuce_enabled && (
                <div>
                  <Label className="text-slate-300">Max Points Per Set (Deuce Cap)</Label>
                  <Input
                    type="number"
                    value={formData.max_points_per_set}
                    onChange={(e) => handleChange('max_points_per_set', parseInt(e.target.value) || 30)}
                    className="bg-slate-800 border-slate-700 mt-2"
                  />
                </div>
              )}
            </div>
          )}

          {/* Step 6: Venue */}
          {currentStep === 6 && (
            <div className="space-y-6">
              <div>
                <Label className="text-slate-300">Venue Name</Label>
                <Input
                  value={formData.venue_name}
                  onChange={(e) => handleChange('venue_name', e.target.value)}
                  placeholder="e.g., Sports Complex"
                  className="bg-slate-800 border-slate-700 mt-2"
                />
              </div>
              <div>
                <Label className="text-slate-300">Address</Label>
                <Textarea
                  value={formData.venue_address}
                  onChange={(e) => handleChange('venue_address', e.target.value)}
                  placeholder="Full address..."
                  className="bg-slate-800 border-slate-700 mt-2"
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <Label className="text-slate-300">City</Label>
                  <Input
                    value={formData.city}
                    onChange={(e) => handleChange('city', e.target.value)}
                    className="bg-slate-800 border-slate-700 mt-2"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">State</Label>
                  <Input
                    value={formData.state}
                    onChange={(e) => handleChange('state', e.target.value)}
                    className="bg-slate-800 border-slate-700 mt-2"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Country</Label>
                  <Input
                    value={formData.country}
                    onChange={(e) => handleChange('country', e.target.value)}
                    className="bg-slate-800 border-slate-700 mt-2"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 7: Entry Fee with Calculation */}
          {currentStep === 7 && (
            <div className="space-y-6">
              <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full border-2 border-green-500 flex items-center justify-center">
                    {!formData.is_free && <div className="w-2.5 h-2.5 rounded-full bg-green-500" />}
                  </div>
                  <span className="text-green-400 font-medium">Challenge Entry fees</span>
                </div>
              </div>
              
              <div>
                <Label className="text-slate-300">Total Entry Fee (₹) - Amount users will pay in app</Label>
                <Input
                  type="number"
                  value={formData.entry_fee}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value) || 0;
                    handleChange('entry_fee', value);
                    handleChange('is_free', value === 0);
                  }}
                  placeholder="E.g., 900 (this is what users pay)"
                  className="bg-slate-800 border-slate-700 mt-2"
                />
                <p className="text-xs text-slate-500 mt-1">Enter the total amount you want users to see and pay in the Lunipin app</p>
              </div>
              
              {!formData.is_free && formData.entry_fee > 0 && (
                <>
                  <div className="p-4 bg-slate-800/50 border border-slate-700 rounded-lg space-y-3">
                    <h4 className="text-white font-medium">Fee Breakdown (Auto-calculated)</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-400 text-sm">Base Fee (Your Revenue)</Label>
                        <div className="bg-slate-800 border border-slate-700 rounded-md p-3 mt-1">
                          <span className="text-white">₹ {(formData.base_entry_fee || 0).toFixed(2)}</span>
                        </div>
                      </div>
                      <div>
                        <Label className="text-slate-400 text-sm">Processing Fee (20% + GST)</Label>
                        <div className="bg-slate-800 border border-slate-700 rounded-md p-3 mt-1">
                          <span className="text-white">₹ {(formData.registration_processing_fee || 0).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-green-400 font-medium">Amount shown in Lunipin App</Label>
                    <div className="bg-green-500/20 border border-green-500/50 rounded-md p-3 mt-2">
                      <span className="text-green-400 font-bold text-lg">₹ {formData.entry_fee.toFixed(2)}</span>
                    </div>
                    <p className="text-xs text-green-400/70 mt-1">✓ This is the exact amount users will see and pay</p>
                  </div>
                </>
              )}
              
              <div>
                <Label className="text-slate-300">Rewards Description</Label>
                <Textarea
                  value={formData.rewards_description}
                  onChange={(e) => handleChange('rewards_description', e.target.value)}
                  placeholder="Describe the rewards for winners..."
                  className="bg-slate-800 border-slate-700 mt-2"
                  rows={3}
                />
              </div>
            </div>
          )}

          {/* Step 8: Prizes */}
          {currentStep === 8 && (
            <div className="space-y-4">
              {formData.prizes.map((prize, index) => (
                <div key={index} className="flex items-center gap-4">
                  <div className={cn(
                    "w-12 h-12 rounded-full flex items-center justify-center font-bold",
                    index === 0 && "bg-yellow-500/20 text-yellow-400",
                    index === 1 && "bg-slate-400/20 text-slate-300",
                    index === 2 && "bg-orange-500/20 text-orange-400"
                  )}>
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <Label className="text-slate-300">{prize.title}</Label>
                    <Input
                      value={prize.prize}
                      onChange={(e) => handlePrizeChange(index, e.target.value)}
                      placeholder="e.g., ₹5000 or Trophy + Medal"
                      className="bg-slate-800 border-slate-700 mt-1"
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Step 9: Banner Upload */}
          {currentStep === 9 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Prize Banner</h3>
                <p className="text-sm text-slate-400 mb-4">Upload an image for your prize/reward</p>
                
                <Label className="text-green-400 font-medium">Prize Banner Image</Label>
                
                <div 
                  onClick={() => bannerInputRef.current?.click()}
                  className="mt-4 border-2 border-dashed border-green-500/50 rounded-full w-48 h-48 mx-auto flex flex-col items-center justify-center cursor-pointer hover:border-green-500 transition-colors"
                >
                  {formData.banner_image ? (
                    <img 
                      src={formData.banner_image} 
                      alt="Banner preview" 
                      className="w-full h-full object-cover rounded-full"
                    />
                  ) : (
                    <>
                      {uploadingBanner ? (
                        <Loader2 className="w-12 h-12 text-green-400 animate-spin" />
                      ) : (
                        <>
                          <Upload className="w-12 h-12 text-green-400 mb-2" />
                          <span className="text-green-400 text-sm">Upload Image</span>
                        </>
                      )}
                    </>
                  )}
                </div>
                
                <input
                  ref={bannerInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleBannerUpload}
                  className="hidden"
                />
                
                <p className="text-center text-green-400 text-sm mt-4 cursor-pointer" onClick={() => bannerInputRef.current?.click()}>
                  Select Image
                </p>
              </div>
              
              <div>
                <Label className="text-slate-300">Or enter Banner URL</Label>
                <Input
                  value={formData.banner_image.startsWith('blob:') ? '' : formData.banner_image}
                  onChange={(e) => handleChange('banner_image', e.target.value)}
                  placeholder="https://..."
                  className="bg-slate-800 border-slate-700 mt-2"
                />
              </div>
            </div>
          )}

          {/* Step 10: Materials Checklist */}
          {currentStep === 10 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Materials Checklist</h3>
                <p className="text-sm text-slate-400 mb-4">Tap items to mark as done</p>
                
                <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg mb-6">
                  <div className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-400" />
                    <span className="text-green-400">{checkedItems} of {totalChecklistItems} items ready</span>
                  </div>
                </div>
              </div>
              
              {/* Essential Items */}
              <div>
                <h4 className="text-sm font-medium text-white mb-3">Essential Items</h4>
                <div className="space-y-2">
                  {sportChecklist.essential.map(item => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => handleChecklistToggle(item.id)}
                      className={cn(
                        "w-full flex items-center gap-3 p-3 rounded-lg border transition-all text-left",
                        formData.materials_checklist?.includes(item.id)
                          ? "border-green-500 bg-green-500/10"
                          : "border-slate-700 bg-slate-800 hover:border-slate-600"
                      )}
                    >
                      <div className={cn(
                        "w-6 h-6 rounded border-2 flex items-center justify-center",
                        formData.materials_checklist?.includes(item.id)
                          ? "border-green-500 bg-green-500"
                          : "border-slate-500"
                      )}>
                        {formData.materials_checklist?.includes(item.id) && (
                          <Check className="w-4 h-4 text-white" />
                        )}
                      </div>
                      <span className="text-xl">{item.icon}</span>
                      <span className="text-white">{item.name}</span>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Optional Items */}
              <div>
                <h4 className="text-sm font-medium text-white mb-3">Optional Items</h4>
                <div className="space-y-2">
                  {sportChecklist.optional.map(item => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => handleChecklistToggle(item.id)}
                      className={cn(
                        "w-full flex items-center gap-3 p-3 rounded-lg border transition-all text-left",
                        formData.materials_checklist?.includes(item.id)
                          ? "border-green-500 bg-green-500/10"
                          : "border-slate-700 bg-slate-800 hover:border-slate-600"
                      )}
                    >
                      <div className={cn(
                        "w-6 h-6 rounded border-2 flex items-center justify-center",
                        formData.materials_checklist?.includes(item.id)
                          ? "border-green-500 bg-green-500"
                          : "border-slate-500"
                      )}>
                        {formData.materials_checklist?.includes(item.id) && (
                          <Check className="w-4 h-4 text-white" />
                        )}
                      </div>
                      <span className="text-xl">{item.icon}</span>
                      <span className="text-white">{item.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 11: Payment Details */}
          {currentStep === 11 && (
            <div className="space-y-6">
              <p className="text-sm text-slate-400">All fields are optional - fill only what's needed</p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Customer Name</Label>
                  <Input
                    value={formData.customer_name}
                    onChange={(e) => handleChange('customer_name', e.target.value)}
                    placeholder="Client/Customer name"
                    className="bg-slate-800 border-slate-700 mt-2"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Vendor Code</Label>
                  <Input
                    value={formData.customer_vendor_code}
                    onChange={(e) => handleChange('customer_vendor_code', e.target.value)}
                    className="bg-slate-800 border-slate-700 mt-2"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-slate-300">Customer Address</Label>
                <Textarea
                  value={formData.customer_address}
                  onChange={(e) => handleChange('customer_address', e.target.value)}
                  className="bg-slate-800 border-slate-700 mt-2"
                  rows={2}
                />
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">GST Number</Label>
                  <Input
                    value={formData.customer_gst_no}
                    onChange={(e) => handleChange('customer_gst_no', e.target.value)}
                    placeholder="e.g., 29ABCDE1234F1Z5"
                    className="bg-slate-800 border-slate-700 mt-2"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">PO Number</Label>
                  <Input
                    value={formData.customer_po_no}
                    onChange={(e) => handleChange('customer_po_no', e.target.value)}
                    className="bg-slate-800 border-slate-700 mt-2"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">PO Date</Label>
                  <Input
                    type="date"
                    value={formData.po_date}
                    onChange={(e) => handleChange('po_date', e.target.value)}
                    className="bg-slate-800 border-slate-700 mt-2"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">PO Amount (₹)</Label>
                  <Input
                    type="number"
                    value={formData.po_amount || ''}
                    onChange={(e) => handleChange('po_amount', parseFloat(e.target.value) || 0)}
                    className="bg-slate-800 border-slate-700 mt-2"
                  />
                </div>
              </div>
              
              <div className="border-t border-slate-700 pt-4">
                <h4 className="text-white font-medium mb-4">Advance Payment</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Amount (₹)</Label>
                    <Input
                      type="number"
                      value={formData.advance_payment_amount || ''}
                      onChange={(e) => handleChange('advance_payment_amount', parseFloat(e.target.value) || 0)}
                      className="bg-slate-800 border-slate-700 mt-2"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Payment Type</Label>
                    <Select value={formData.advance_payment_type || "none"} onValueChange={(v) => handleChange('advance_payment_type', v === "none" ? "" : v)}>
                      <SelectTrigger className="bg-slate-800 border-slate-700 mt-2">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        <SelectItem value="none">Select type</SelectItem>
                        {PAYMENT_TYPES.map(t => (
                          <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              
              <div className="border-t border-slate-700 pt-4">
                <h4 className="text-white font-medium mb-4">Final Payment</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Amount (₹)</Label>
                    <Input
                      type="number"
                      value={formData.final_payment_amount || ''}
                      onChange={(e) => handleChange('final_payment_amount', parseFloat(e.target.value) || 0)}
                      className="bg-slate-800 border-slate-700 mt-2"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Payment Type</Label>
                    <Select value={formData.final_payment_type || "none"} onValueChange={(v) => handleChange('final_payment_type', v === "none" ? "" : v)}>
                      <SelectTrigger className="bg-slate-800 border-slate-700 mt-2">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        <SelectItem value="none">Select type</SelectItem>
                        {PAYMENT_TYPES.map(t => (
                          <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              
              <div className="border-t border-slate-700 pt-4">
                <h4 className="text-white font-medium mb-4">Invoice Details</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Invoice Number</Label>
                    <Input
                      value={formData.invoice_no}
                      onChange={(e) => handleChange('invoice_no', e.target.value)}
                      className="bg-slate-800 border-slate-700 mt-2"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Invoice Date</Label>
                    <Input
                      type="date"
                      value={formData.invoice_date}
                      onChange={(e) => handleChange('invoice_date', e.target.value)}
                      className="bg-slate-800 border-slate-700 mt-2"
                    />
                  </div>
                </div>
              </div>
              
              <div>
                <Label className="text-slate-300">Other Details</Label>
                <Textarea
                  value={formData.other_details}
                  onChange={(e) => handleChange('other_details', e.target.value)}
                  placeholder="Additional details (up to 1000 chars)"
                  maxLength={1000}
                  className="bg-slate-800 border-slate-700 mt-2"
                  rows={3}
                />
                <p className="text-xs text-slate-500 mt-1">{formData.other_details?.length || 0}/1000</p>
              </div>
            </div>
          )}

          {/* Step 12: Review */}
          {currentStep === 12 && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-white">Challenge Details</h3>
                  <div className="space-y-2 text-sm">
                    <p><span className="text-slate-400">Name:</span> <span className="text-white">{formData.name}</span></p>
                    <p><span className="text-slate-400">Sport:</span> <span className="text-white">{selectedSport?.name} ({formData.format_type})</span></p>
                    <p><span className="text-slate-400">Scope:</span> <span className="text-white capitalize">{formData.scope}</span></p>
                    {formData.group_name && <p><span className="text-slate-400">Group:</span> <span className="text-white">{formData.group_name}</span></p>}
                    <p><span className="text-slate-400">Teams:</span> <span className="text-white">{formData.min_teams} - {formData.max_teams}</span></p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-white">Tournament Settings</h3>
                  <div className="space-y-2 text-sm">
                    <p><span className="text-slate-400">Type:</span> <span className="text-white capitalize">{formData.tournament_type}</span></p>
                    <p><span className="text-slate-400">Scoring:</span> <span className="text-white">Best of {formData.sets_to_win * 2 - 1}, {formData.points_per_set} points</span></p>
                    <p><span className="text-slate-400">Deuce:</span> <span className="text-white">{formData.deuce_enabled ? 'Enabled' : 'Disabled'}</span></p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-white">Dates</h3>
                  <div className="space-y-2 text-sm">
                    <p><span className="text-slate-400">Event:</span> <span className="text-white">{formData.event_start || 'Not set'} to {formData.event_end || 'Not set'}</span></p>
                    <p><span className="text-slate-400">Registration:</span> <span className="text-white">{formData.registration_start || 'Not set'} to {formData.registration_end || 'Not set'}</span></p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-white">Entry & Prizes</h3>
                  <div className="space-y-2 text-sm">
                    <p><span className="text-slate-400">Entry Fee (User pays):</span> <span className="text-green-400 font-medium">{formData.is_free ? 'Free' : `₹${formData.entry_fee}`}</span></p>
                    {!formData.is_free && <p><span className="text-slate-400">Base Fee (Your revenue):</span> <span className="text-white">₹{(formData.base_entry_fee || 0).toFixed(2)}</span></p>}
                    {!formData.is_free && <p><span className="text-slate-400">Processing Fee:</span> <span className="text-white">₹{(formData.registration_processing_fee || 0).toFixed(2)}</span></p>}
                    {formData.prizes.filter(p => p.prize).map(p => (
                      <p key={p.position}><span className="text-slate-400">{p.title}:</span> <span className="text-white">{p.prize}</span></p>
                    ))}
                  </div>
                </div>
              </div>
              
              {formData.venue_name && (
                <div className="p-4 bg-slate-800 rounded-lg">
                  <h3 className="text-lg font-semibold text-white mb-2">Venue</h3>
                  <p className="text-slate-300">{formData.venue_name}</p>
                  <p className="text-sm text-slate-400">{formData.venue_address}</p>
                  <p className="text-sm text-slate-400">{formData.city}, {formData.state}, {formData.country}</p>
                </div>
              )}
              
              {checkedItems > 0 && (
                <div className="p-4 bg-slate-800 rounded-lg">
                  <h3 className="text-lg font-semibold text-white mb-2">Materials Checklist</h3>
                  <p className="text-green-400">{checkedItems} of {totalChecklistItems} items marked as ready</p>
                </div>
              )}
              
              <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                <p className="text-green-400 text-sm">
                  <strong>Note:</strong> This challenge will be marked as an official admin-created challenge. Click "Create Challenge" to save and you can publish it later from the Events page.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Navigation Buttons */}
      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={prevStep}
          disabled={currentStep === 1}
          className="border-slate-700"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Previous
        </Button>
        
        {currentStep < STEPS.length ? (
          <Button
            onClick={nextStep}
            className="bg-green-500 hover:bg-green-600 text-slate-900"
          >
            Next
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            disabled={loading}
            className="bg-green-500 hover:bg-green-600 text-slate-900"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
            Create Challenge
          </Button>
        )}
      </div>
    </div>
  );
}
